import { Entries, Person } from '@eformulieren/model'
import type { LoginOption } from '@eformulieren/renderer'
import type { FormContext } from './formContext.js'

/**
 * De stand van de contextberekening bij het opkomen van de pagina (M5.16d, R-154).
 *
 * **Waarom dit een apart, `window`-vrij bestand is.** `main.tsx` leest `window.__proef`,
 * `sessionStorage` en het pad synchroon en roept daarna, als dat niets oplevert, een netwerk in.
 * Dat orkestreren hoort bij de component; de beslissingen zelf (wat betekent een basis zonder
 * persoon, wat betekent een mislukte `/whoami`) staan hier als pure functies, zodat ze getoetst
 * kunnen worden zonder een DOM: geen `window`, geen React, alleen invoer en uitvoer.
 *
 * Vijf standen, en een zesde ('fout' tijdens het invullen zelf) hoort hier niet: dat blijft de
 * `Stand` van `Formulierpagina.tsx`, over de verbinding met de pod en niet over de vraag wie
 * er kijkt. Twee kwamen er met opdracht 5c bij: `inloggen`, met een knop per ingang van de pod
 * (`inloggen.ts`), en `storing`, als de pod niet kon vaststellen of er een sessie is.
 */
export type ContextState =
  | { readonly soort: 'gereed'; readonly context: FormContext }
  /**
   * Er is een instantie (uit het pad) maar nog geen `persoon`: `/whoami` is onderweg.
   *
   * Geen "geen context" en geen leeg formulier tijdens deze stand (valkuil 6): de instantie
   * staat al vast, alleen wie ernaar kijkt nog niet. `data-proef="wachten-op-persoon"` en niet
   * `"wachten"`: dat laatste is al bezet door `FormPage`'s eigen wachtscherm op de
   * samenwerkverbinding (`e2e/directe-link.spec.ts` steunt op het onderscheid).
   */
  | { readonly soort: 'wachten'; readonly instantieId: string }
  | { readonly soort: 'geen-context' }
  /** Geen sessie voor deze publicatie: een knop per ingang naar de inlogdienst (opdracht 5c). */
  | { readonly soort: 'inloggen'; readonly opties: readonly LoginOption[] }
  /** Niet vast te stellen of er een sessie is; inloggen helpt dan niet (opdracht 5c). */
  | { readonly soort: 'storing' }

/**
 * De stand meteen bij het opkomen van de pagina, vóór enig netwerkverzoek.
 *
 * **`basis` wint, ongewijzigd.** Heeft `window.__proef` of `sessionStorage` al een volledige
 * context (de bestaande weg: een proef, een e2e-opstelling, of ooit een gastheer), dan is er
 * niets te wachten en niets op te halen; dat is dezelfde synchrone uitkomst als vóór deze
 * opdracht, zonder de extra renderslag die een `wachten`-stand anders zou geven.
 *
 * **Geen basis, wel een instantie uit het pad: wachten op `/whoami`.** Dit is de echte directe
 * link zonder gastheer: er is niets op `window` gezet, alleen het HttpOnly-sessiecookie dat de
 * browser al meestuurt. De instantie staat al vast (`instantieFromPath`), dus die hoeft niet nog
 * eens uit het antwoord te komen.
 *
 * **Geen basis en geen instantie: ongewijzigd meteen "geen context".** Precies het gedrag van
 * vóór deze opdracht; er is niets om op te wachten en dus geen reden voor een extra verzoek.
 */
export function beginContextState(
  base: FormContext | undefined,
  instantieId: string | undefined,
): ContextState {
  if (base !== undefined) return { soort: 'gereed', context: base }
  if (instantieId === undefined) return { soort: 'geen-context' }
  return { soort: 'wachten', instantieId }
}

/**
 * Wat `/whoami` zei, in de drie uitkomsten die voor de pagina iets anders betekenen (opdracht 5c).
 *
 * Tot opdracht 5c waren het er twee, een persoon of niets, en dan stond er een melding. Nu leidt
 * "geen sessie" naar de keuze om in te loggen, en dat mag alleen bij een 401: bij een 503 (de pod
 * bereikt de identiteitsprovider niet, `token.ts`) of zonder verbinding helpt inloggen niet, want
 * met een lopende sessie stuurt de inlogdienst meteen terug en staat de gebruiker hier weer.
 */
export type Whoami =
  | { readonly soort: 'persoon'; readonly persoon: string; readonly verlooptOp?: number; readonly ingang?: Entries }
  | { readonly soort: 'geen-sessie' }
  | { readonly soort: 'onbekend' }

/**
 * De stand na `/whoami`, voor de instantie die al vaststond toen het verzoek begon.
 *
 * Alleen voor een persoon en voor een onbekende uitkomst: zonder sessie vraagt de pagina eerst de
 * ingangen op, en `zonderSessie` in `inloggen.ts` maakt daar de stand van.
 */
export function afterWhoami(
  instantieId: string,
  outcome: Exclude<Whoami, { readonly soort: 'geen-sessie' }>,
  prefill: Readonly<Record<string, string>>,
): ContextState {
  if (outcome.soort === 'onbekend') return { soort: 'storing' }
  return {
    soort: 'gereed',
    context: {
      persoon: outcome.persoon,
      instantieId,
      voorvulling: prefill,
      ...(outcome.verlooptOp === undefined ? {} : { verlooptOp: outcome.verlooptOp }),
      ...(outcome.ingang === undefined ? {} : { ingang: outcome.ingang }),
    },
  }
}

/**
 * Het lijf van een geslaagde `/whoami`, getoetst: de persoon tegen `Persoon`, de vervaltijd als eindig
 * getal, en de eigen ingang tegen `Ingangen` (WCAG 2.2.1, R-139). Past de ingang niet, dan valt alleen
 * die weg: de persoon en de vervaltijd blijven, en de pagina verlengt dan langs `/ingangen`.
 */
export function whoamiFromBody(body: unknown): Whoami {
  const read = (typeof body === 'object' && body !== null ? body : {}) as { persoon?: unknown; verlooptOp?: unknown; ingang?: unknown }
  const found = Person.safeParse(read.persoon)
  if (!found.success) return { soort: 'onbekend' }
  const expires = typeof read.verlooptOp === 'number' && Number.isFinite(read.verlooptOp) ? read.verlooptOp : undefined
  const entry = Entries.safeParse(read.ingang)
  return {
    soort: 'persoon',
    persoon: found.data,
    ...(expires === undefined ? {} : { verlooptOp: expires }),
    ...(entry.success ? { ingang: entry.data } : {}),
  }
}

/**
 * Wie het token achter deze sessie zegt dat hij is, van `GET /whoami` (`leesendpoints.ts`).
 *
 * **`credentials: 'include'`, en geen `Authorization`-header.** Er is op dit punt geen context
 * en dus geen token om als header mee te sturen; dat is precies de reden dat dit verzoek nodig
 * is. Het HttpOnly-sessiecookie gaat vanzelf mee omdat de pod zijn eigen pagina serveert
 * (dezelfde oorsprong); expliciet en niet stilzwijgend, zodat dat niet van de default afhangt.
 *
 * **Nooit een fout.** Een 401 is "geen sessie"; een 503, een netwerkfout en een antwoord dat niet op
 * de vorm past zijn "onbekend". De pod heeft de oorzaak al in zijn eigen log staan (`server.ts`).
 *
 * Gevalideerd tegen `Persoon` en niet zomaar doorgegeven: het antwoord komt van de eigen pod,
 * maar wat er over de lijn komt is JSON en geen vertrouwde waarde totdat de vorm is getoetst.
 */
export async function getWhoami(): Promise<Whoami> {
  try {
    const response = await fetch('/whoami', { credentials: 'include' })
    if (response.status === 401) return { soort: 'geen-sessie' }
    if (!response.ok) return { soort: 'onbekend' }
    return whoamiFromBody(await response.json())
  } catch {
    return { soort: 'onbekend' }
  }
}
